import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import useStyles from './useStyles';

export type Props = React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>;

/**
 * BypassBlock for accessible skip links
 * @example
 * ```ts
 * <BypassBlock className={optionalOverrideClasses}>
 *   <a href="#main-content">Skiip to Content</a>
 * </BypassBlock>
 * // ...
 * <main id="main-content" tabIindex={-1} />
 * ```
 */
function BypassBlock({ children, className, ...restProps }: Props): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const [isFocused, setVisibility] = useState(false);

  const { skipLinks } = useStyles({ isFocused });
  useEffect(() => {
    const bypassRef = ref.current as HTMLElement;

    /**
     * Check if the current focused element is a child of _this_ container.
     * This is a programmatic equivalent to CSS `:focus-within` whiich is
     * not supported in <= IE 11
     */
    const handleFocus = (e: FocusEvent): void => {
      if (bypassRef.contains(document.activeElement as HTMLElement)) {
        setVisibility(true);
      } else {
        setVisibility(false);
      }
    };
    bypassRef.addEventListener('focusin', handleFocus);
    bypassRef.addEventListener('focusout', handleFocus);
    return () => {
      bypassRef.removeEventListener('focusin', handleFocus);
      bypassRef.removeEventListener('focusout', handleFocus);
    };
  }, [ref]);
  return (
    <div ref={ref} className={classNames(skipLinks, className)} {...restProps}>
      {children}
    </div>
  );
}

export default BypassBlock;
